import type {
  DateRange,
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionStatsByVehicleSummaryByPeriod,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
} from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { CardProvider, Configuration, FleetFuelIntegration, FuelTransaction } from '../..';
import type { FuelTransactionResponse } from '../../__generated__/graphql';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import { toSaveFuelTransactionBody } from './APITransformers';
import type { UpdateTransactionRequest } from './Definitions/APITypes';
import type { CreateIntegrationRequestBody, IntegrationRequestResponse, RequestedCnpj } from './Definitions/Types';
import { FLEET_FUEL_INTEGRATION_STATE_ACTIVE, FLEET_FUEL_INTEGRATION_STATE_REQUESTED } from './Definitions/Types';
import { normalizeCnpj } from './normalizeCnpj';

const isCardProvider = (value: unknown): value is CardProvider => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.slug === 'string' && typeof row.displayName === 'string' && typeof row.logoKey === 'string' && typeof row.requiresKeyPerCnpj === 'boolean';
};

// A 2xx that is not a provider array would throw inside render, past the point React Query can
// flip the query to `isError` — blank screen instead of the banner already wired to `queryFn`.
const assertCardProviderCatalogue = (payload: unknown): CardProvider[] => {
  if (!(Array.isArray(payload) && payload.every(isCardProvider))) {
    throw new Error('GET /fuel/card-providers did not answer with a card provider catalogue');
  }
  return payload;
};

const isFleetFuelIntegrationPartner = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.slug === 'string' && typeof row.displayName === 'string' && typeof row.logoKey === 'string';
};

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

const isFleetFuelIntegration = (value: unknown): value is FleetFuelIntegration => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    isFleetFuelIntegrationPartner(row.provider) &&
    isNullableString(row.cnpj) &&
    (row.state === FLEET_FUEL_INTEGRATION_STATE_ACTIVE || row.state === FLEET_FUEL_INTEGRATION_STATE_REQUESTED) &&
    typeof row.stateDate === 'string' &&
    isNullableString(row.requestedByName) &&
    isNullableString(row.integrationId) &&
    isNullableString(row.requestCnpjId) &&
    isNullableString(row.lastOutcome)
  );
};

// E1 is fail-closed (telas/00-E1.jpg): a partial or malformed 2xx must reach E2 exactly like a
// network error or a timeout, never render as if the account had no integrations.
const assertFleetFuelIntegrations = (payload: unknown): FleetFuelIntegration[] => {
  if (!(Array.isArray(payload) && payload.every(isFleetFuelIntegration))) {
    throw new Error('GET /fuel/integrations did not answer with a fleet integration list');
  }
  return payload;
};

const isRequestedCnpj = (value: unknown): value is RequestedCnpj => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string' && typeof row.cnpj === 'string' && typeof row.companyName === 'string' && typeof row.state === 'string';
};

const assertIntegrationRequestResponse = (payload: unknown): IntegrationRequestResponse => {
  const fail = () => new Error('POST /fuel/integration-requests did not answer with an integration request');
  if (typeof payload !== 'object' || payload === null) {
    throw fail();
  }
  const row = payload as Record<string, unknown>;
  const isValid =
    typeof row.requestId === 'string' &&
    isNullableString(row.integrationId) &&
    Array.isArray(row.cnpjs) &&
    row.cnpjs.every(isRequestedCnpj) &&
    Array.isArray(row.alreadyIntegrated) &&
    row.alreadyIntegrated.every((cnpj) => typeof cnpj === 'string');
  if (!isValid) {
    throw fail();
  }
  return payload as IntegrationRequestResponse;
};

// E1's screen spec (telas/00-E1.jpg) leaves the exact value to the dev team, only specifying that
// it triggers on network error, timeout or a 5xx response. 8s keeps a broken backend from holding
// the loading dialog open indefinitely without cutting off a herbie-api response that is merely slow.
const FLEET_INTEGRATIONS_TIMEOUT_MS = 8_000;

/** Panel filters forwarded to `/transactions/report`, mirroring the ones the on-screen queries use. */
export interface TransactionsReportFilters {
  fuelTypes?: string[];
  vehicleIds?: string[];
  vehicleSizes?: string[];
  tagNames?: string[];
  searchQuery?: string;
}

export class NewFuelCardRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getTransactionsHistory = (body: TransactionsHistoryBodyContent): Promise<PagedTransactions> => {
    const requestData: IRequest = {
      endpoint: '/transactions/list',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactions;
    });
  };

  public getTransactionsSummary = (body: TransactionSummaryBodyContent): Promise<TransactionsSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSummary;
    });
  };

  public getTransactionsAvgFuelConsumptionAndCostPerKm = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsAvgFuelConsumptionAndCostPerKm[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/avg-fuel-consumption-and-cost-per-km',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsAvgFuelConsumptionAndCostPerKm[];
    });
  };

  public getTransactionsSumTotalCostByFuelType = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsSumTotalCostByFuelType[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/sum-total-cost-by-fuel-type',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSumTotalCostByFuelType[];
    });
  };

  public getTransactionStatsByVehicleSummary = (body: TransactionSummaryBodyContent): Promise<TransactionStatsByVehicleSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionStatsByVehicleSummary;
    });
  };

  /**
   * Variante por período de `/transactions/stats-by-vehicle/summary`: endpoint dedicado
   * `/stats-by-vehicle/summary/grouped`, com `group_period` obrigatório no body. Retorna
   * um array — um item por bucket (semana/mês). O caller agregado continua em
   * `getTransactionStatsByVehicleSummary` no endpoint sem `/grouped`.
   */
  public getTransactionStatsByVehicleSummaryByPeriod = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionStatsByVehicleSummaryByPeriod[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle/summary/grouped',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionStatsByVehicleSummaryByPeriod[];
    });
  };

  public getTransactionStatsByVehicle = (body: TransactionsHistoryBodyContent): Promise<PagedTransactionStatsByVehicle> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactionStatsByVehicle;
    });
  };

  /** Partner catalogue (FND-06): server-side filtered to visible rows and ordered by display name. */
  public getCardProviders = async (): Promise<CardProvider[]> => {
    const requestData: IRequest = {
      endpoint: '/fuel/card-providers',
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    return assertCardProviderCatalogue(camelcaseKeys(response.data, { deep: true }));
  };

  /**
   * The read behind `E1`/`T1` (FND-07): what the fleet already has with each partner, active or
   * requested. `request()` sets a fresh `AbortController` synchronously before the promise settles
   * (see `Requester.request`), so it is read right after the call, into a local variable — not
   * lazily inside the timer callback.
   *
   * The timeout races `responsePromise` instead of only aborting it: `Requester._request` awaits
   * `sessionManager.maybeRenewAccessToken()` before the signal ever reaches axios, so a hung token
   * renewal would leave `abort()` with nothing in flight to cancel and this promise would never
   * settle — exactly the case the timeout exists to cover. Racing means `getFleetIntegrations()`
   * itself always settles by `FLEET_INTEGRATIONS_TIMEOUT_MS`, regardless of where the underlying
   * request is stuck; `abort()` is still called, best-effort, for whichever stage it does reach.
   */
  public getFleetIntegrations = async (): Promise<FleetFuelIntegration[]> => {
    const requestData: IRequest = {
      endpoint: '/fuel/integrations',
      method: HttpMethod.GET,
    };

    const responsePromise = this.requester.request(requestData);
    const controller = this.requester.getAbortController();
    // A response arriving after the race already timed out would otherwise reject unobserved.
    responsePromise.catch(() => {
      /* noop */
    });

    let timer: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      // Reject once the timeout elapses, so a hung backend or token renewal surfaces the same way a
      // network error would, instead of holding the loading dialog open forever.
      timer = setTimeout(() => {
        controller?.abort();
        reject(new Error('GET /fuel/integrations timed out'));
      }, FLEET_INTEGRATIONS_TIMEOUT_MS);
    });

    try {
      const response = await Promise.race([responsePromise, timeoutPromise]);
      return assertFleetFuelIntegrations(camelcaseKeys(response.data, { deep: true }));
    } finally {
      clearTimeout(timer!);
    }
  };

  /**
   * `T7`'s submit (FND-08): creates the request, its CNPJs and the consent in one call — the
   * backend writes all three in a single transaction, so there is no separate consent endpoint to
   * call afterwards. `deep: true` on both directions because `requester`/`decider`/`consent` are
   * nested objects, not a flat body.
   */
  public createIntegrationRequest = async (body: CreateIntegrationRequestBody): Promise<IntegrationRequestResponse> => {
    const normalizedBody = { ...body, cnpjs: body.cnpjs.map((entry) => ({ ...entry, cnpj: normalizeCnpj(entry.cnpj) })) };
    const requestData: IRequest = {
      endpoint: '/fuel/integration-requests',
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(normalizedBody, { deep: true })),
    };

    const response = await this.requester.request(requestData);
    return assertIntegrationRequestResponse(camelcaseKeys(response.data, { deep: true }));
  };

  public getFuelTransaction = async (transactionId: string): Promise<FuelTransaction> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    return camelcaseKeys(response.data, { deep: true }) as FuelTransaction;
  };

  /**
   * The panel filters have to travel with the export. `/transactions/report` reads the very same
   * filter set as the on-screen queries, but treats an absent one as "no filter at all" — so a
   * request carrying only the period yields the unfiltered superset, and the spreadsheet stops
   * agreeing with the numbers the user is looking at (BUG-2780: `Outros` showing up under a
   * Gasolina/Diesel filter, and a plate repeating once per fuel type in the summary tab).
   *
   * Empty filters are omitted rather than sent as `[]`: both mean "no filter" to the backend, and
   * leaving them out keeps an unfiltered export's payload identical to what it has always been.
   */
  public getTransactionsReport = (period: DateRange, vehicleGroupIds: string[], filters?: TransactionsReportFilters): Promise<Blob> => {
    const requestData: IRequest = {
      endpoint: '/transactions/report',
      method: HttpMethod.POST,
      body: JSON.stringify({
        start_date: new Date(period.start),
        end_date: new Date(period.end),
        vehicle_group_ids: vehicleGroupIds,
        ...(filters?.fuelTypes?.length ? { fuel_types: filters.fuelTypes } : {}),
        ...(filters?.vehicleIds?.length ? { vehicle_ids: filters.vehicleIds } : {}),
        ...(filters?.vehicleSizes?.length ? { vehicle_sizes: filters.vehicleSizes } : {}),
        ...(filters?.tagNames?.length ? { tag_names: filters.tagNames } : {}),
        ...(filters?.searchQuery ? { search_query: filters.searchQuery } : {}),
      }),
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  };

  /**
   * Replaces the legacy `PUT /fuel/transactions/deactivate`, which is served by the old Scala
   * frentista-api. Besides the path, three things differ and none of them is cosmetic: the method
   * is POST, the body is a bare array of ids instead of `{ transaction_ids: [...] }`, and the
   * backend answers 204 No Content.
   */
  public deleteTransactions = (transactionIds: string[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/transactions/delete',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionIds),
    };

    return this.requester.request(requestData).then();
  };

  public updateFuelTransaction = (transactionId: string, updateRequest: UpdateTransactionRequest): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.PUT,
      body: JSON.stringify(updateRequest),
    };

    return this.requester.request(requestData).then();
  };

  public createFuelTransaction = (createRequest: FuelTransactionBodyContent): Promise<FuelTransactionResponse> => {
    const transactionBody = toSaveFuelTransactionBody(createRequest);
    const requestData: IRequest = {
      endpoint: '/transactions/create',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionBody),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as FuelTransactionResponse;
    });
  };

  /**
   * Backend `/transactions/create` only accepts JSON, so the odometer photo is encoded the same way
   * as the receipt: base64 bytes inside `odometer_file_base64` (+ matching `odometer_file_content_type`).
   * The `File` is read here so callers can keep passing it as a `File` object without doing the
   * base64 dance themselves.
   */
  public createFuelTransactionWithOdometerFile = async (createRequest: FuelTransactionBodyContent, odometerFile: File): Promise<FuelTransactionResponse> => {
    const { base64, contentType } = await readFileAsBase64(odometerFile);
    return this.createFuelTransaction({
      ...createRequest,
      odometerFileBase64: base64,
      odometerFileContentType: contentType,
    });
  };
}

/** Reads a `File` into a `{base64, contentType}` pair — no `data:...;base64,` prefix. */
const readFileAsBase64 = (file: File): Promise<{ base64: string; contentType: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const commaIdx = result.indexOf(',');
      resolve({
        base64: commaIdx >= 0 ? result.slice(commaIdx + 1) : result,
        contentType: file.type || 'application/octet-stream',
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

export default NewFuelCardRequester;
